import { ItemAvailability, Offer, OfferShippingDetails, Product, WithContext, MerchantReturnPolicy, DefinedRegion, ReturnMethodEnumeration } from 'schema-dts'
import { getPriceInclTax, TaxClass, getBundleSelectionIdsForVariation } from '@rogue/universal'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useProductStore } from '@/js/stores/productStore'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import { getBuyRequestMeta, getSimpleForBuyRequest, recreateBundleBuyRequest } from '@/js/helpers/buy-request'
import { BuyRequest, BuyRequestMeta, IRogue } from '@/js/services/RogueTypes'

type ReviewsPartial = Pick<WithContext<Product>, 'reviews'|'aggregateRating'>

type FeedVariationEntry = {
  id?: string
  title?: string
  subTitle?: string
  mpn?: string
  color?: string
  gtin?: string
  mainImage?: string
  additionalImages?: string[]
  lifestyleImage?: string
}

function getReviews(visibleId: number | undefined, rogue: IRogue): ReviewsPartial {
  const retval: ReviewsPartial = { }

  if (!visibleId) {
    return retval
  }

  const product = rogue.getProductById(visibleId)

  if (!product) {
    return retval
  }

  const visibleSku = product.sku.toUpperCase()
  const reviewsStore = useReviewsStore(rogue.pinia)
  const { summaries, reviews } = reviewsStore

  const summary = summaries[visibleSku]
  if (summary?.count > 0) {
    retval.aggregateRating = {
      '@type'     : 'AggregateRating',
      ratingValue : summary.average,
      reviewCount : summary.count,
      bestRating  : 5,
      worstRating : 1,
    }
  }

  const customerReviews = reviews[visibleSku]
  if (customerReviews?.length) {
    retval.reviews = customerReviews
      .slice(0)
      .sort((r1, r2) => r2.rating - r1.rating)
      .filter(r => r.author?.name && r.rating > 2)
      .map(r => ({
        '@context'    : 'https://schema.org',
        '@type'       : 'Review',
        name          : r.title,
        author        : { '@type': 'Person', name: r.author.name },
        datePublished : r.date,
        itemReviewed  : product.name,
        reviewBody    : r.text,
        reviewRating  : {
          '@type'     : 'Rating',
          ratingValue : `${r.rating}`,
          worstRating : '1',
          bestRating  : '5',
        },
        publisher: { '@type': 'Organization', name: 'Rogue Fitness' },
      }))
  }

  return retval
}

function getOffer(meta: BuyRequestMeta, rogue: IRogue, variantID?: number): Offer {
  try {
    const variant = rogue.getProductById(Number(variantID))
    const product =  variant || rogue.getProductById(meta?.id) || rogue.getProductById(Number(meta.groupId))

    let variantPrice

    if (variant?.hasPromo) {
      variantPrice = variant.attributes.rogue_promo_price
    } else if (variant?.special_price) {
      variantPrice = variant.special_price
    } else {
      variantPrice = variant?.price
    }

    // If the offer is for a variant, we want to use that price
    let price = variantPrice || meta?.total || Number(product?.price)
    if(meta?.stockStatus === 'bin' && meta.bundleSku && meta.basePrice) {
      price = meta.basePrice
    }

    if (rogue.site.code === 'EUR' || rogue.site.code === 'GBR') {
      const taxClassVal = product.attributes?.tax_class_id as TaxClass
      price = getPriceInclTax(Number(price), taxClassVal ?? TaxClass.EUGoods, rogue.l10n.country)
    }

    let availability:ItemAvailability = 'https://schema.org/InStock'
    if (meta.stockStatus === 'bin' || meta.stockStatus === 'oos') {
      availability = 'https://schema.org/OutOfStock'
    }

    let returnsDays = 30
    const returnMethods: ReturnMethodEnumeration[] = ['ReturnByMail']

    if (['EUR', 'GBR'].includes(rogue.site.code)) {
      returnsDays = 14
    }

    if (['USA'].includes(rogue.site.code)) {
      returnMethods.push('ReturnInStore')
    }

    // hasMerchantReturnPolicy is a type from schema.org and is not a boolean despite it's name
    const hasMerchantReturnPolicy: MerchantReturnPolicy[] = [
      {
        '@type'              : 'MerchantReturnPolicy',
        merchantReturnLink   : `${rogue.localizedBaseURL}/return-policies`,
        applicableCountry    : rogue.l10n.country,
        returnPolicyCategory : product?.attributes?.is_returnable ? 'https://schema.org/MerchantReturnFiniteReturnWindow' : 'https://schema.org/MerchantReturnNotPermitted',
        merchantReturnDays   : product?.attributes?.is_returnable ? returnsDays : 0,
        returnMethod         : product?.attributes?.is_returnable ? returnMethods : undefined,
        returnFees           : product?.attributes?.is_returnable ? 'ReturnShippingFees' : undefined,
        itemCondition        : product?.attributes?.is_returnable ? 'https://schema.org/NewCondition' : undefined,
      },
    ]

    const shipsFromUS = !product?.sku.startsWith('EU') && (meta.flags?.heavyDirect || meta.flags?.lightDirect)

    const isDropShip = product?.attributes?.shipperhq_shipping_group
      ?.some(category => ['DROPSHIP', 'PER_ORDER_DROPSHIP', 'DROPSHIP_FREIGHT'].includes(category))

    let addressCountry = 'US'
    let addressRegion = 'OH'
    let postalCode = '43201'

    if (rogue.site.code === 'AUS') {
      addressCountry = 'AU'
      addressRegion = 'Australian Capital Territory'
      postalCode = '2560'
    } else if (['EUR', 'GBR'].includes(rogue.site.code) && !shipsFromUS) {
      addressCountry = 'BE'
      addressRegion = 'Antwerpen'
      postalCode = '2627'
    }

    const shippingOriginDetails: DefinedRegion = {
      '@type': 'DefinedRegion',
      addressCountry,
      addressRegion,
      postalCode,
    }

    const shippingDetails: OfferShippingDetails = {
      '@type'              : 'OfferShippingDetails',
      shippingOrigin       : !isDropShip ? shippingOriginDetails : undefined,
      shippingSettingsLink : `${rogue.localizedBaseURL}/shippingtracker`,
      weight               : product?.weight ? { '@type': 'QuantitativeValue', value: product.weight } : undefined,
    }

    if (meta.flags?.freeShip || meta.flags?.freeCartShip) {
      shippingDetails.shippingRate = {
        '@type'  : 'MonetaryAmount',
        value    : '0',
        currency : rogue.site.currency,
      }
    }

    const retval:Offer = {
      '@type'       : 'Offer',
      priceCurrency : rogue.l10n.currency,
      itemCondition : 'https://schema.org/NewCondition',
      price         : `${price}`,
      availability,
      hasMerchantReturnPolicy,
      shippingDetails,
      seller        : {
        '@type' : 'Organization',
        name    : 'Rogue Fitness',
      },
    }

    return retval

  } catch (error) {
    const url = rogue.router?.currentRoute?.value?.fullPath ?? 'unknown'
    const product =  rogue.getProductById(meta?.id) || rogue.getProductById(Number(meta.groupId))
    rogue.log?.warn(`[ldjson] getOffer failed for product "${product}" at ${url}`, error)

    return {
      '@type'       : 'Offer',
      priceCurrency : rogue.l10n.currency,
      price         : meta?.total ? String(meta.total) : undefined,
      seller        : {
        '@type' : 'Organization',
        name    : 'Rogue Fitness',
      },
    }
  }
}

// Builds hasVariant entries for plp_variation_thumbnail_pattern bundles (rackbuilder).
// Emits one variant per unique color value (plp_variation_attribute) — matching the feed repo's
// generateConfigurableVariations, which also produces one entry per color rather than a cross-product.
async function getBundleColorVariants(bundleBuyRequest: BuyRequest, rogue: IRogue): Promise<object[]> {
  const bundleProduct = rogue.getProductById(bundleBuyRequest.productId)
  const simple = getSimpleForBuyRequest(bundleBuyRequest, rogue)
  const bundleOptions = bundleProduct?.bundleOptions || []

  if (!bundleOptions.length) return []

  const productStore = useProductStore(rogue.pinia)
  if (!productStore.allChildrenLoaded(bundleBuyRequest.productId)) {
    await productStore.loadAllChildren(bundleBuyRequest.productId, rogue)
  }

  // Find the color configurable option (the one with plp_variation_attribute).
  let variationAttributeCode = ''
  let colorConfigurableId: number | null = null

  for (const option of bundleOptions) {
    const defaultVal = option.values?.find(v => v.isDefault) ?? (option.isRequired ? option.values?.[0] : null)
    if (!defaultVal) continue
    const product = rogue.getProductById(defaultVal.productId)
    if (product?.type === 'configurable' && product.attributes?.plp_variation_attribute) {
      variationAttributeCode = product.attributes.plp_variation_attribute as string
      colorConfigurableId = product.productId
      break
    }
  }

  if (!colorConfigurableId) return []

  // Filter color children by rack_depth/rack_height to match the current simple's dimensions.
  const FORCE_ATTRS = {
    rack_depth  : simple?.attributes?.rack_depth,
    rack_height : simple?.attributes?.rack_height,
  }

  function matchesForcedAttrs(product: ReturnType<typeof rogue.getProductById>): boolean {
    return Object.entries(FORCE_ATTRS).every(
      ([attr, val]) => val === undefined || product.attributes[attr] === undefined || product.attributes[attr] === val
    )
  }

  const colorConfigurable = rogue.getProductById(colorConfigurableId)
  const seenColors = new Set<string>()
  const colorChildren = (colorConfigurable?.childrenIds || [])
    .map(id => rogue.getProductById(id))
    .filter(p => p != null && matchesForcedAttrs(p) && !!p.attributes?.[variationAttributeCode])
    .filter(p => {
      const color = p.attributes[variationAttributeCode] as string
      if (seenColors.has(color)) return false
      seenColors.add(color)

      return true
    })

  if (!colorChildren.length) return []

  const repository = { getProductById: (id: number) => rogue.getProductById(id) }

  const meta = getBuyRequestMeta(bundleBuyRequest, rogue)
  const rawLinkPath = meta.linkPath?.split('?')[0]
  const pagePath = rawLinkPath?.startsWith('/') ? rawLinkPath : undefined
  const variants: object[] = []

  for (const colorChild of colorChildren) {
    const color = colorChild.attributes[variationAttributeCode] as string

    // Delegate all swap logic (primary color, secondConfig, nameplate) to @rogue/universal —
    // the single source of truth shared with the feed pipeline.
    const selectionForRecreate = getBundleSelectionIdsForVariation(bundleProduct, repository, color)

    // Fresh clone per iteration — recreateBundleBuyRequest mutates in place.
    const bundleBuyRequestClone = JSON.parse(JSON.stringify(bundleBuyRequest))
    recreateBundleBuyRequest(bundleBuyRequestClone, rogue, selectionForRecreate)
    const variantMeta = getBuyRequestMeta(bundleBuyRequestClone, rogue)

    // Build URL with trailing zeros trimmed — mirrors feed repo's generateConfigurableVariations
    // which strips optional upsell slots with no default.
    const trimmedSelection = [...selectionForRecreate]
    while (trimmedSelection.length && trimmedSelection[trimmedSelection.length - 1] === 0) {
      trimmedSelection.pop()
    }
    const bundleParams = trimmedSelection.length ? `?b=${trimmedSelection.join('-')}` : ''
    const variantUrl = pagePath ? `${rogue.localizedBaseURL}${pagePath}${bundleParams}` : undefined

    variants.push({
      '@type' : 'Product',
      name    : meta.name,
      sku     : colorChild.sku,
      color,
      image   : variantMeta.thumbnail || undefined,
      url     : variantUrl,
      offers  : [getOffer(variantMeta, rogue)],
    })
  }

  return variants
}

// Builds hasVariant entries for feed_variation_pattern bundles (cave, MMS). Prices come from the feed.
async function getFeedVariationVariants(bundleBuyRequest: BuyRequest, rogue: IRogue, baseLinkPath?: string) {
  const bundleProduct = rogue.getProductById(bundleBuyRequest.productId)
  const feedVariationPattern = bundleProduct?.attributes?.feed_variation_pattern as string | undefined

  if (!feedVariationPattern) {
    return { variants: [] }
  }

  let feedVariations: Record<string, FeedVariationEntry>
  try {
    feedVariations = JSON.parse(feedVariationPattern) as Record<string, FeedVariationEntry>
  } catch (e) {
    rogue.log.warn('Unable to parse feed_variation_pattern for LDJSON', e)

    return { variants: [] }
  }

  // Ensure the bundle's option products are in the store before computing prices.
  // On SSR, loadAllChildren for the grouped parent doesn't load the bundle's deeper children.
  const productStore = useProductStore(rogue.pinia)
  if (!productStore.allChildrenLoaded(bundleBuyRequest.productId)) {
    await productStore.loadAllChildren(bundleBuyRequest.productId, rogue)
  }

  const meta = getBuyRequestMeta(bundleBuyRequest, rogue)
  const groupedProduct = bundleBuyRequest.metadata?.groupedProductId
    ? rogue.getProductById(bundleBuyRequest.metadata.groupedProductId)
    : undefined
  const baseName = groupedProduct?.name || bundleProduct?.name || meta.name
  const pagePath = (baseLinkPath || meta.linkPath)?.split('?')[0]

  // Fetch per-variant and base prices from the feed.
  const priceByRecipeId: Record<string, number | undefined> = {}
  let basePrice: number | undefined

  if (bundleProduct?.sku) {
    try {
      type FeedEntry = { id: string, price: string, link: string }
      const feedData = await rogue.fetch<FeedEntry[] | { entries: FeedEntry[] }>(
        `/svc_feed/feed?site=${rogue.site.code}&include=simple&dest=google&format=json&addBundleVariations=true&sku=${bundleProduct.sku}`
      )
      const entries: FeedEntry[] = Array.isArray(feedData) ? feedData : (feedData?.entries ?? [])

      for (const entry of entries) {
        const ridMatch = entry.link?.match(/[?&]rid=([^&]+)/)
        const recipeId = ridMatch?.[1]
        const price = entry.price ? parseFloat(entry.price) : NaN

        if (isNaN(price)) continue

        if (recipeId) {
          priceByRecipeId[recipeId] = price
        } else if (basePrice === undefined) {
          // First entry without ?rid is the canonical base product price
          basePrice = price
        }
      }
    } catch (e) {
      rogue.log.warn('ldjson: failed to fetch feed for variant prices', e)
    }
  }

  const variants = Object.entries(feedVariations).map(([key, entryData]) => {
    const recipeId = key.startsWith('rid-') ? key.slice(4) : key
    const variantUrl = pagePath ? `${rogue.localizedBaseURL}${pagePath}?rid=${recipeId}` : undefined
    const variantName = entryData.title || entryData.subTitle || baseName
    const variantImage = entryData.mainImage
      ? getCloudinaryUrl(entryData.mainImage, 1600, undefined, '#ffffff') as string
      : undefined
    const variantMeta = priceByRecipeId[recipeId] != null
      ? { ...meta, total: priceByRecipeId[recipeId] }
      : meta

    return {
      '@type' : 'Product',
      name    : variantName,
      sku     : entryData.mpn || undefined,
      mpn     : entryData.mpn || undefined,
      image   : variantImage,
      color   : entryData.color || undefined,
      gtin    : entryData.gtin || undefined,
      url     : variantUrl,
      offers  : [getOffer(variantMeta, rogue)],
    }
  })

  // When a specific recipe is active, prefer its own price over the no-rid base entry.
  const currentRid = rogue.router?.currentRoute.value.query.rid as string | undefined
  const effectivePrice = (currentRid ? priceByRecipeId[currentRid] : undefined) ?? basePrice
  const baseMeta = effectivePrice != null ? { ...meta, total: effectivePrice } : undefined

  return { variants, baseMeta }
}


export async function getLDJsonForProduct(buyRequest: BuyRequest, rogue: IRogue): Promise<WithContext<Product>> {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const visible = rogue.getProductById(meta.linkId)
  const simple = rogue.getProductById(meta?.id)
  let groupSimpleName = ''
  let groupSimpleSKU = ''
  let groupSimpleProductID = ''
  let groupSimpleURL = ''
  const mainProduct = visible || rogue.getProductById(Number(meta?.groupId)) || rogue.getProductById(meta?.id)
  const mainProductID = mainProduct.productId.toString()
  const simpleProductID = simple?.productId.toString()

  // ?sku= means a specific variant is explicitly selected — treat as single Product, not ProductGroup. Unlike ?b= which is a ProductGroup
  const skuFromUrl = rogue.router?.currentRoute.value.query?.sku as string | undefined                                                
  const querySKU = !!skuFromUrl                                                                                                       

  // This is schema.orgs terminology that needs to map on to our product types
  const isProductGroup = (meta.groupId || mainProduct.type === 'configurable' || mainProduct.type === 'grouped') && !querySKU

  const mainSku = meta.configSku || meta.sku || meta.groupSku || meta.linkSku

  // If this is a grouped product, we may need to change the name etc to the specific simple... for example if they
  // selected one wind screen, we don't want to show data about the Echo Bike
  if (meta.groupId) {
    const group = rogue.getProductById(meta.groupId)
    // Check if this group product has a primary product + accessories (like Echo Bike), or if all are equal (like Bumpers)
    const variantSelected = (buyRequest.children || []).find(child => child.qty > 0)                                                                  
        || (skuFromUrl                                                                                                                              
          ? (buyRequest.children || []).find(child =>                                                                                               
            rogue.getProductById(child.productId)?.sku?.toUpperCase() === skuFromUrl.toUpperCase()                                                  
          )                                                                                                                                         
          : undefined)    
                                                                                                                                                    
    if(variantSelected) {                                                                                                                         
      const groupSimpleProduct = rogue.getProductById(variantSelected.productId)     
                                     
      groupSimpleName = groupSimpleProduct?.name                                                                                                  
      groupSimpleSKU = groupSimpleProduct?.sku || ''                                                                                              
      groupSimpleProductID = groupSimpleProduct?.productId.toString() || ''                                                                       
      groupSimpleURL = visible ? `${visible.url}?sku=${groupSimpleSKU}` : ''                                                                                    
    } 

    // This is a group page, but we have a sku selected, so return the selected product's ldjson
    if (variantSelected && querySKU) {
      const copy  = JSON.parse(JSON.stringify(variantSelected))
      if (copy.metadata) {
        copy.metadata.groupedProductId = undefined
      }

      const childLDJson = await getLDJsonForProduct(copy, rogue)
      // Grouped products with visibility 2 never set linkId, so `visible` is undefined and the group's own url is the fallback.
      const groupUrl = (visible || group)?.url

      if (!childLDJson.url && childLDJson.sku && groupUrl) {
        childLDJson.url = `${rogue.localizedBaseURL}${groupUrl}?sku=${childLDJson.sku}`
      }

      // The copy no longer knows its parents, so a simple without its own thumbnail falls back to the nearest parent's (configurable, then group)
      const parentThumbnail = rogue.getProductById(buyRequest.productId)?.thumbnail || group?.thumbnail
      if (!(childLDJson.image as string[])?.length && parentThumbnail) {
        childLDJson.image = [getCloudinaryUrl(parentThumbnail, 1600, undefined, '#ffffff') as string]
      }

      return childLDJson
    }
  }

  const categoryStore = useCategoryStore(rogue.pinia)

  const { aggregateRating, reviews } = getReviews(visible?.productId, rogue)

  let image:string[] = (visible?.gallery || []).slice(0, 5).map((entry) => getCloudinaryUrl(entry.link, 1600, undefined, '#ffffff') as string)

  if (!image.length && (mainProduct.thumbnail || meta.thumbnail)) {
    image = [getCloudinaryUrl(mainProduct.thumbnail || meta.thumbnail, 1600, undefined, '#ffffff') as string]
  }

  const primaryCategory = categoryStore.categories[mainProduct.primaryCategoryId]
  let categoryBreadCrumbs
  if (primaryCategory?.path) {
    categoryBreadCrumbs = [...primaryCategory.path.map(crumb => crumb.name), primaryCategory.name].join(' > ')
  }

  const productDescription = mainProduct?.attributes?.meta_description

  const productColor = simple?.attributes?.color?.toString()
  || simple?.attributes?.rig_color?.toString()
  || simple?.attributes?.merchant_color?.toString()
  || simple?.attributes?.color_standard?.toString()
  || simple?.attributes?.color?.toString()
  || mainProduct?.attributes?.bumper_color?.toString()
  || mainProduct?.attributes?.color?.toString()
  || simple?.attributes?.reebok_shoe_colors?.toString()
  || simple?.attributes?.shirt_color?.toString()
  || simple?.attributes?.hat_color?.toString() || ''


  const productMaterial = simple?.attributes?.adlinfo_material || mainProduct.attributes?.bumper_material

  const productHeight = simple?.attributes?.adlinfo_height || simple?.attributes?.ship_height
  const formattedProductHeight = productHeight ? parseFloat(productHeight) : undefined

  const productWidth = simple?.attributes?.adlinfo_width || simple?.attributes?.ship_width
  const formattedProductWidth = productWidth ? parseFloat(String(productWidth)) : undefined

  const productSize = simple?.attributes?.generic_size || simple?.attributes?.shoe_size
  const formattedProductSize = parseFloat(String(productSize))
  // Product sizes are going to be a quantitativeValue therefore shoe and apparrel sizes will be excluded
  const isApparelOrShoes =  meta.categories.includes('Shoes') || meta.categories.includes('Apparel')

  const productDepth = parseFloat(String(mainProduct.attributes?.rack_depth))
  const productWeight = simple?.weight

  const childVariants = mainProduct.childrenIds || []
  let variants = [] as number[]
  let bundleVariants: Product[] = []
  let configVariants: Product[]  = []

  // Get the variants for all configurables
  let configurableProduct = mainProduct
  const visitedIds = new Set<number>()
  
  while (configurableProduct && configurableProduct.type !== 'configurable') {
    const nextProductId = configurableProduct.childrenIds?.[0]
    if (!nextProductId) break

    const nextId = Number(nextProductId)
    if (visitedIds.has(nextId)) {
      rogue.log.error(`[ldjson] cycle detected walking childrenIds chain; aborting traversal`, {
        url                : rogue?.router?.currentRoute?.value?.fullPath ?? 'unknown',
        mainProductName    : mainProduct?.name,
        mainProductSku     : mainProduct?.sku,
        mainProductId      : mainProduct?.productId,
        cycleAtProductId   : nextId,
        currentProductId   : configurableProduct?.productId,
        currentProductSku  : configurableProduct?.sku,
        currentProductType : configurableProduct?.type,
      })
      break
    }
    visitedIds.add(nextId)

    configurableProduct = rogue.getProductById(nextId)
    variants = configurableProduct?.childrenIds || []
  }

  const bundleBuyRequest = buyRequest.type === 'bundle'
    ? buyRequest
    : (buyRequest.children || []).find(child => child.type === 'bundle')

  let feedBaseMeta: BuyRequestMeta | undefined

  if (bundleBuyRequest) {
    const bundleProduct = rogue.getProductById(bundleBuyRequest.productId)

    if (bundleProduct?.attributes?.feed_variation_pattern) {
      // feed_variation_pattern bundles (e.g. MMS): variants are defined as recipes, not bundle option cross-products.
      try {
        const result = await getFeedVariationVariants(bundleBuyRequest, rogue, meta.linkPath)
        bundleVariants = result.variants as Product[]
        feedBaseMeta = result.baseMeta
      } catch (e) {
        rogue.log.warn('Unable to generate feed variation variants for LDJSON', e)
        bundleVariants = []
      }
    } else if (bundleProduct?.attributes.plp_variation_thumbnail_pattern) {
      // Rackbuilder bundles: one variant per unique color value, matching the feed pipeline.
      try {
        bundleVariants = await getBundleColorVariants(bundleBuyRequest, rogue) as Product[]
      } catch (e) {
        rogue.log.warn('Unable to generate bundle color variants for LDJSON', e)
        bundleVariants = []
      }
    }
    // Standard bundles (no feed_variation_pattern, no plp_variation_thumbnail_pattern) generate no hasVariant
    // entries — matching the feed pipeline which deletes variations for these products.
  }

  if (variants.length < 1) {
    variants = childVariants
  }

  if (isProductGroup && variants.length > 0 && bundleVariants.length === 0) {
    // populate config variants
    configVariants = variants.map((variant) => {
      const product = rogue.getProductById(variant)
      const childBuyRequest = buyRequest.children?.find(c => c.productId === variant)
      const variantMeta = childBuyRequest ? getBuyRequestMeta(childBuyRequest, rogue) : meta
      let imageVariant

      if ((product?.thumbnail || meta?.thumbnail)) {
        imageVariant = getCloudinaryUrl(product.thumbnail || meta.thumbnail, 1600, undefined, '#ffffff')
      }

      // Use the variant's own URL if it has one, otherwise fall back to
      // the parent configurable's path with ?sku= to match how the frontend selects variants.
      const ownPath = variantMeta?.linkPath?.startsWith('/') && variantMeta.linkPath !== meta.linkPath?.split('?')[0]
        ? variantMeta.linkPath
        : undefined
      const parentPath = meta.linkPath?.startsWith('/') ? meta.linkPath.split('?')[0] : undefined
      let variantUrl: string | undefined

      if (ownPath) {
        variantUrl = `${rogue.localizedBaseURL}${ownPath}`
      } else if (parentPath) {
        variantUrl = `${rogue.localizedBaseURL}${parentPath}?sku=${product?.sku}`
      }

      return {
        '@type' : 'Product',
        name    : product?.name,
        sku     : product?.sku,
        image   : imageVariant || undefined,
        color   : product.attributes?.rig_color?.toString() || product.attributes?.color?.toString() || product.attributes?.color_standard?.toString() || undefined,
        url     : variantUrl,
        offers  : [getOffer(variantMeta, rogue, product?.productId)],
      }
    })
  }

  let productVariants

  if (configVariants.length > 0) {
    productVariants = configVariants
  } else if (bundleVariants.length > 0) {
    productVariants = bundleVariants
  }
  
  const ldjson: WithContext<Product> = {
    '@context'        : 'https://schema.org',
    '@type'           : isProductGroup ? 'ProductGroup' : 'Product',
    sku               : isProductGroup ? mainSku : simple?.sku || groupSimpleSKU || mainSku,
    mpn               : isProductGroup ? mainSku : simple?.sku || groupSimpleSKU || mainSku,
    productGroupID    : isProductGroup ? mainSku : undefined,
    productID         : isProductGroup ? mainProductID : simpleProductID || groupSimpleProductID || mainProductID,
    name              : isProductGroup ? meta.name : simple?.name || groupSimpleName || meta.name,
    model             : meta.name,
    image,
    offers            : [getOffer(feedBaseMeta ?? meta, rogue, Number(groupSimpleProductID) || undefined)],
    hasVariant        : productVariants,
    description       : productDescription || undefined,
    category          : categoryBreadCrumbs || (categoryStore.categories[mainProduct.primaryCategoryId] || {}).name || '',
    brand             : mainProduct.attributes.manufacturer ? { '@type': 'Brand', name: mainProduct.attributes.manufacturer } : undefined,
    url               : visible ? `${rogue.localizedBaseURL}${groupSimpleURL || visible.url}` : undefined,
    aggregateRating,
    reviews,
    color             : productColor || undefined,
    material          : productMaterial || undefined,
    height            : formattedProductHeight ? { '@type': 'QuantitativeValue', value: formattedProductHeight } : undefined,
    width             : formattedProductWidth ? { '@type': 'QuantitativeValue', value: formattedProductWidth } : undefined,
    depth             : productDepth ? { '@type': 'QuantitativeValue', value: productDepth } : undefined,
    size              : formattedProductSize && !isApparelOrShoes ? { '@type': 'QuantitativeValue', value: formattedProductSize } : undefined,
    weight            : productWeight ? { '@type': 'QuantitativeValue', value: productWeight } : undefined,
    manufacturer      : mainProduct.attributes.manufacturer ? mainProduct.attributes.manufacturer : undefined,
    countryOfAssembly : mainProduct.attributes.made_in_usa === true ? 'USA' : undefined,
  }

  return ldjson
}
